import { useState } from 'react';
import { useFormikContext } from 'formik';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { extractFromAnyMessage } from './extractors';
import { EditOrderFormValues } from '../EditOrderDialog';
import { useAuth } from '../../../../session/InternalAuthProvider';
import { AccountData, OrderPageTypes } from '../../../../api';
import { useEditOrder } from '../EditOrderContext';

type SnackbarState = {
  open: boolean;
  severity: 'success' | 'error';
  message: string;
};

const closed: SnackbarState = { open: false, severity: 'success', message: '' };

function timeStringToSeconds(time: string): number {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 3600 + minutes * 60;
}

export function usePrefillFromClipboard() {
  const { setFieldValue } = useFormikContext<EditOrderFormValues>();
  const { t } = useTranslation();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  // @ts-ignore
  const { editOrderState, setOrderDetailValue, setDate, setPrice } = useEditOrder();
  const isPrivateSession = ![OrderPageTypes.EVENT, OrderPageTypes.COURSE].includes(
    editOrderState.orderPage.order_page_type,
  );
  const [snackbar, setSnackbar] = useState<SnackbarState>(closed);

  const showError = () => setSnackbar({ open: true, severity: 'error', message: t('externalPrefill.prefillFailed') });

  const handlePrefill = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const text = await navigator.clipboard.readText();
      const data = extractFromAnyMessage(text);
      console.log(data);
      if (!data.firstName && !data.lastName && !data.date && !data.time) {
        showError();
        return;
      }

      if (data.firstName) {
        void setFieldValue('firstname', data.firstName);
      }
      if (data.lastName) {
        void setFieldValue('lastname', data.lastName);
      }
      if (data.date) {
        void setFieldValue('date', data.date);
        setDate(moment(data.date));
      }
      if (data.persons) {
        setOrderDetailValue('persons', data.persons);
      }
      if (data.time) {
        const newTimeSeconds = timeStringToSeconds(data.time);
        void setFieldValue('timeSeconds', newTimeSeconds);
        if (isPrivateSession) {
          void setFieldValue('privateOrderEndTimeSeconds', newTimeSeconds + editOrderState.details.duration);
        }
      }

      const contact = account.business_details?.contact;
      if (contact?.email) {
        void setFieldValue('email', contact.email);
      }
      const phone = data.phone ?? contact?.phone;
      if (phone) {
        void setFieldValue('phone', phone);
      }
      if (data.source) {
        void setFieldValue('comment', t(data.source));
      }
      setPrice(0);

      setSnackbar({ open: true, severity: 'success', message: t('externalPrefill.prefillSuccess') });
    } catch {
      showError();
    }
  };

  const closeSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));

  return { handlePrefill, snackbar, closeSnackbar };
}
